import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, Navigate, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import ValidationSummary from '../../../Shared/ValidationSummary';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../../../Shared/TextFieldFormGroup';
import GeneralError from '../../../Shared/GeneralError';
import useFetch, { Status } from '../../../../hooks/fetch.hook';
import type IValidatable from '../../../Shared/validatable.interface';
import ValidationInfo from '../../../../validation/validation-info';
import { ValidatableComponent, ValidationHelper } from '../../../../helpers/validation.helper';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import { AnalyticsService } from '../../../../services/analytics-service';
import MunicipalityApiModel from '../../models/municipality.api-model';
import ParticipationApiModel from '../../models/participation.api-model';
import SelectSchool, { type ISelectSchoolState } from '../SelectSchool';
import { CompetitionApiModel } from '../../models/competition.api-model';
import Spinner from '../../../Shared/Spinner';
import { SystemClock } from '../../services/system-clock.service';
import Breadcumb from '../Breadcrumb';
import SchoolApiModel from '../../models/school.api-model';
import SelectFormGroup, { type ISelectFormGroupState } from '../../../Shared/SelectFormGroup';
import { UserApiModel } from '../../models/user.api-model';
import CheckboxFormGroup, { type ICheckboxFormGroupState } from '../../../Shared/CheckboxFormGroup';
import PageIntroduction from '../../Shared/PageIntroduction';
import type IParticipation from '../../../../models/pa-egna-ben/participation.model';
import { Role } from '../../../../models/pa-egna-ben/role.enum';
import type ICompetition from '../../../../models/pa-egna-ben/competition.model';
import InformationBox from '../../Shared/InformationBox';
import { MunicipalitySelectorModalHelper } from '../../../../../components/municipality-selector-modal/municipality-selector-modal.helper';
import { SelectSchoolField, SelectSchoolMode } from '../SelectSchool/select-school.enum';
import { InformationBoxType } from '../InformationBox/information-box-type.enum';

function RegisterInCompetition({ role }: { role: Role }) {
  // === HOOKS ===
  const { municipalityGid } = useParams();

  // === STATE ===
  const [nameState, setNameState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [numberOfStudentsState, setNumberOfStudentsState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [phoneNumberState, setPhoneNumberState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [selectedMunicipalityState, setSelectedMunicipalityGid] = useState<ISelectFormGroupState>({ value: municipalityGid, validationInfo: new ValidationInfo() });
  const [selectSchoolState, setSelectSchoolState] = useState<ISelectSchoolState>({
    value: {
      mode: SelectSchoolMode.chooseinlist,
      school: {
        id: -1,
        name: '',
        streetAddress: '',
        postalCode: '',
        city: '',
        municipalityGid: undefined,
      },
    },
    validationInfo: new ValidationInfo(),
  });
  const [integrityPolicyAcceptedState, setIntegrityPolicyAcceptedState] = useState<ICheckboxFormGroupState>({ value: [], validationInfo: new ValidationInfo() });
  const [saveStatus, setSaveStatus] = useState<Status>(Status.notperformed);
  const [saveErrorCode, setSaveErrorCode] = useState<number | undefined>(undefined);
  const [saveParticipationResponse, setParticipationResponseStatus] = useState<IParticipation>();

  // Get current user
  const [getUser] = useFetch<UserApiModel>(`/api/paegnaben/${role}/current`);
  const [{ status: getCurrentCompetitionStatus, data: getCurrentCompetitionData }] = useFetch<CompetitionApiModel>(`/api/paegnaben/${role}/competitions/current`);
  const [{ status: getMunicipalitiesStatus, data: getMunicipalitiesData }] = useFetch<MunicipalityApiModel[]>(`/api/paegnaben/${role}/municipalities/participating`);

  // === REFS ===
  const nameRef = useRef<IValidatable>(null!);
  const numberOfStudentsRef = useRef<IValidatable>(null!);
  const phoneNumberRef = useRef<IValidatable>(null!);
  const municipalityRef = useRef<IValidatable>(null!);
  const schoolRef = useRef<IValidatable>(null!);
  const integrityPolicyRef = useRef<IValidatable>(null!);

  // === DATA ===
  const user = getUser.data ? UserApiModel.mapToBusinessModel(getUser.data) : undefined;
  const municipalities = getMunicipalitiesData?.map(MunicipalityApiModel.mapToBusinessModel);
  const currentCompetition: ICompetition | undefined = getCurrentCompetitionData ? CompetitionApiModel.mapToBusinessModel(getCurrentCompetitionData) : undefined;

  // === INTERFACE IMPLEMENTATION ===
  function getValidatableComponents(): {
    component: IValidatable;
    validationInfo: ValidationInfo;
    setState: React.Dispatch<React.SetStateAction<{ value: unknown; validationInfo: ValidationInfo }>>;
  }[] {
    const result: ValidatableComponent[] = [
      { component: nameRef.current, validationInfo: nameState.validationInfo, setState: setNameState },
      { component: numberOfStudentsRef.current, validationInfo: numberOfStudentsState.validationInfo, setState: setNumberOfStudentsState },
    ];
    if (role === Role.teacher && !user?.phoneNumber) {
      result.push({ component: phoneNumberRef.current, validationInfo: phoneNumberState.validationInfo, setState: setPhoneNumberState });
    }
    result.push({ component: municipalityRef.current, validationInfo: selectedMunicipalityState.validationInfo, setState: setSelectedMunicipalityGid });
    result.push({ component: schoolRef.current, validationInfo: selectSchoolState.validationInfo, setState: setSelectSchoolState });
    if (role === Role.teacher) {
      result.push({ component: integrityPolicyRef.current, validationInfo: integrityPolicyAcceptedState.validationInfo, setState: setIntegrityPolicyAcceptedState });
    }
    return result;
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, AnalyticsService.ErrorAction, e);
    });
  }

  // === EVENT HANDLERS ===
  async function handleSaveButtonClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    let isFormValid: boolean = false;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (saveStatus === Status.loading) return;
      setSaveStatus(Status.notperformed);
      setSaveErrorCode(undefined);
      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(role === Role.teacher ? AnalyticsService.PaEgnaBenTeacher : AnalyticsService.PaEgnaBenAdmin, AnalyticsService.ErrorAction, e);
        });
        return;
      }
      setSaveStatus(Status.loading);
      const participationApiModel: ParticipationApiModel = {
        id: -1,
        name: nameState.value,
        numberOfStudents: numberOfStudentsState.value ? parseInt(numberOfStudentsState.value, 10) : 0,
        schoolId: selectSchoolState.value.mode === SelectSchoolMode.chooseinlist && selectSchoolState.value.school ? selectSchoolState.value.school.id : -1,
        school: selectSchoolState.value.mode === SelectSchoolMode.createnew && selectSchoolState.value.school ? SchoolApiModel.mapToApiModel(selectSchoolState.value.school) : undefined,
      };
      const antiForgeryTokenElement = document.getElementById('antiForgeryToken') as HTMLInputElement;
      try {
        const response = await fetch(`/api/paegnaben/${role}/participations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': antiForgeryTokenElement.value },
          body: JSON.stringify({
            participation: participationApiModel,
            phoneNumber: phoneNumberState.value,
            integrityPolicyAccepted: integrityPolicyAcceptedState.value.includes('accepted'),
          }),
        });
        if (response.ok) {
          const responseData = await response.json();
          setParticipationResponseStatus(ParticipationApiModel.mapToBusinessModel(responseData));
          setSaveStatus(Status.success);
          submitResult = { status: Status.success, errorCode: undefined };
          AnalyticsService.addEvent(role === Role.teacher ? AnalyticsService.PaEgnaBenTeacher : AnalyticsService.PaEgnaBenAdmin, 'Anmälan skapad', '');
        } else {
          setSaveStatus(Status.failed);
          setSaveErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
          }
          AnalyticsService.addEvent(role === Role.teacher ? AnalyticsService.PaEgnaBenTeacher : AnalyticsService.PaEgnaBenAdmin, 'Anmälan skapad - misslyckat', '');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setSaveStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: error.status };
      }
    });
    if (!isFormValid || submitResult?.errorCode === 400) {
      document.getElementById('form')?.scrollIntoView();
      document.getElementById('form-validation-summary')?.focus();
    }
  }

  // === EFFECTS ===
  useEffect(() => {
    if (!municipalityGid) {
      const selectedMunicipality = MunicipalitySelectorModalHelper.getSelectedMunicipalityFromCookies();
      if (selectedMunicipality && selectedMunicipality.gid !== MunicipalitySelectorModalHelper.NO_SELECTED_MUNICIPALITY_GID) {
        setSelectedMunicipalityGid({ value: selectedMunicipality.gid, validationInfo: new ValidationInfo() });
      }
    }
  }, [municipalityGid]);

  // === VARIABLES ===
  const now = SystemClock.now();
  const isLoadingData = getUser.status === Status.loading || getCurrentCompetitionStatus === Status.loading || getMunicipalitiesStatus === Status.loading;
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === HELPER FUNCTIONS ===
  function isRegistrationAllowed(): boolean {
    if (currentCompetition === undefined) return false;
    if (role === Role.municipalityAdministrator) {
      const firstLeg = currentCompetition?.legs?.find((l) => l.number === 1);
      if (firstLeg === undefined) return false;
      return currentCompetition?.registrationStartTime < now && now < firstLeg?.startTime;
    }
    return currentCompetition?.registrationStartTime < now && currentCompetition?.registrationEndTime > now;
  }

  // === RENDER ===
  if (getUser.httpResponseStatusCode === 401 || (saveStatus === Status.failed && saveErrorCode === 401)) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getUser.httpResponseStatusCode === 403 || (saveStatus === Status.failed && saveErrorCode === 403)) {
    return <Navigate to="/atkomst-saknas" />;
  }

  if (saveStatus === Status.success) {
    const search = saveParticipationResponse?.id ? `id=${saveParticipationResponse.id}` : '';
    const pathname = role === Role.teacher ? '/' : `/kommuner/${municipalityGid}`;
    return <Navigate to={{ pathname, search }} />;
  }

  return (
    <div>
      {role === Role.teacher && (
        <Breadcumb
          items={[
            { text: 'Mina sidor', url: '/' },
            { text: 'Anmäl klass', url: undefined },
          ]}
        />
      )}
      {role === Role.municipalityAdministrator && (
        <Breadcumb
          items={[
            { text: 'Mina sidor', url: '/' },
            { text: municipalities?.find((x) => x.gid === municipalityGid)?.name ?? municipalityGid ?? '', url: `/kommuner/${municipalityGid}` },
            { text: 'Anmäl klass', url: undefined },
          ]}
        />
      )}
      <PageIntroduction heading="Anmäl klass" />
      {isLoadingData && <Spinner />}
      {getCurrentCompetitionStatus === Status.success && !isRegistrationAllowed() && (
        <div>
          <p>Anmälan till årets upplaga av På egna ben öppnar {currentCompetition?.registrationStartTime.toLocaleString(DateTime.DATE_SHORT)}.</p>
        </div>
      )}
      {getCurrentCompetitionStatus === Status.success && !isRegistrationAllowed() && (
        <div>
          <p>Anmälan till årets upplaga av På egna ben är stängd.</p>
        </div>
      )}
      {getCurrentCompetitionStatus === Status.failed && <GeneralError className="Något gick fel, kontakta din lokala projektledare om problemet kvarstår." />}
      {!isLoadingData && isRegistrationAllowed() && (
        <>
          {role === Role.municipalityAdministrator && (
            <InformationBox
              type={InformationBoxType.Information}
              headingElement={<h2 className="h3">Lägg till pedagog</h2>}
              textElement={<div>När klassen är anmäld kan du lägga till en ansvarig pedagog genom att redigera klassens uppgifter.</div>}
              className="mb-4 mb-md-5"
            />
          )}
          <form id="form">
            <fieldset>
              <legend className="visually-hidden">Anmälningsformulär</legend>
              <h2 className="h3">Klassuppgifter</h2>
              <div>
                <ValidationSummary id="form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
                <TextFieldFormGroup
                  ref={nameRef}
                  labelText="Namn"
                  required={true}
                  hideRequiredAsterisk={true}
                  inputId="name-input"
                  type="text"
                  placeholder="Exempelvis 5C"
                  serverValidationName="name"
                  maxLength={35}
                  state={[nameState, setNameState]}
                />
                <TextFieldFormGroup
                  ref={numberOfStudentsRef}
                  labelText="Antal elever i klassen"
                  required={true}
                  hideRequiredAsterisk={true}
                  inputId="number-of-students-input"
                  type="number"
                  serverValidationName="numberOfStudents"
                  state={[numberOfStudentsState, setNumberOfStudentsState]}
                />
                {role === Role.teacher && !user?.phoneNumber && (
                  <TextFieldFormGroup
                    ref={phoneNumberRef}
                    labelText="Telefonnummer till pedagog"
                    required={true}
                    hideRequiredAsterisk={true}
                    inputId="phone-number-input"
                    type="text"
                    serverValidationName="phoneNumber"
                    maxLength={17}
                    helpTextElement={<span>Ditt telefonnummer sparas under Mina uppgifter</span>}
                    state={[phoneNumberState, setPhoneNumberState]}
                  />
                )}
                <SelectFormGroup
                  ref={municipalityRef}
                  serverValidationName="municipalityGid"
                  id="municipality-select"
                  labelText="Kommun"
                  placeholder="Välj kommun"
                  required={true}
                  hideRequiredAsterisk={true}
                  options={
                    municipalities?.map((m) => ({
                      text: m.name,
                      value: m.gid,
                    })) ?? []
                  }
                  state={selectedMunicipalityState}
                  onChange={(x) => {
                    setSelectedMunicipalityGid({ value: x, validationInfo: new ValidationInfo() });
                    setSelectSchoolState({
                      value: { mode: SelectSchoolMode.chooseinlist, school: { id: -1, name: '', streetAddress: '', postalCode: '', city: '', municipalityGid: x } },
                      validationInfo: new ValidationInfo(),
                    });
                  }}
                />
                <SelectSchool
                  ref={schoolRef}
                  municipalityGid={selectedMunicipalityState.value}
                  inputId="school-select"
                  state={selectSchoolState}
                  onChange={(value) =>
                    setSelectSchoolState((ps) => ({
                      ...ps,
                      value: value ?? { mode: SelectSchoolMode.chooseinlist, school: { id: -1, name: '', streetAddress: '', postalCode: '', city: '', municipalityGid: municipalityGid! } },
                    }))
                  }
                  onKeyUp={(e) => {
                    if (e === SelectSchoolField.mode) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(),
                      }));
                    }
                    if (e === SelectSchoolField.existingSchool) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                          ...ps.validationInfo.children,
                          existingSchool: new ValidationInfo(),
                        }),
                      }));
                    }
                    if (e === SelectSchoolField.name) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                          ...ps.validationInfo.children,
                          schoolName: new ValidationInfo(),
                        }),
                      }));
                    }
                    if (e === SelectSchoolField.streetAddress) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                          ...ps.validationInfo.children,
                          schoolStreetAddress: new ValidationInfo(),
                        }),
                      }));
                    }
                    if (e === SelectSchoolField.postalCode) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                          ...ps.validationInfo.children,
                          schoolPostalCode: new ValidationInfo(),
                        }),
                      }));
                    }
                    if (e === SelectSchoolField.city) {
                      setSelectSchoolState((ps) => ({
                        ...ps,
                        validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                          ...ps.validationInfo.children,
                          schoolCity: new ValidationInfo(),
                        }),
                      }));
                    }
                  }}
                />
              </div>
              {role === Role.teacher && (
                <CheckboxFormGroup
                  labelText="Integritetspolicy"
                  hideLabel={true}
                  ref={integrityPolicyRef}
                  required={true}
                  serverValidationName="integrityPolicyAccepted"
                  options={[
                    {
                      value: 'accepted',
                      text: 'Jag godkänner att mina personuppgifter behandlas i enlighet med <a href="https://www.vasttrafik.se/om-vasttrafik/integritetspolicy/" target="_blank" rel="noopener noreferrer">Västtrafiks integritetspolicy</a>',
                    },
                  ]}
                  allowUnsafeHtml={true}
                  state={integrityPolicyAcceptedState}
                  onChange={(value) => setIntegrityPolicyAcceptedState({ value, validationInfo: new ValidationInfo() })}
                />
              )}
              <div className="d-grid d-md-flex gap-3">
                <Link to={`/${role === Role.municipalityAdministrator ? `kommuner/${municipalityGid}` : ''}`} className="btn btn-secondary flex-grow-1 flex-basis-0 order-2 order-md-1">
                  Avbryt
                </Link>
                <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0 order-1 order-md-2" onClick={handleSaveButtonClick}>
                  Anmäl klass
                </button>
              </div>
              {saveStatus === Status.failed && saveErrorCode !== 400 && <GeneralError errorText="Kunde inte spara anmälan. Kontakta projektledaren i din kommun." className="mt-3" />}
            </fieldset>
          </form>
        </>
      )}
    </div>
  );
}

export default RegisterInCompetition;
